import React from "react";
import { Tractor, Scissors, Leaf, Mountain, Home, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Precision Lawn Mowing",
  description: "Premium cylinder and rotary mowing for a golf-course finish. We specialise in clean stripes and healthy turf growth that elevates your property's curb appeal.",
  icon: Tractor,
  colSpan: "md:col-span-2",
  accentColor: "var(--color-primary)",
  hoverBorder: "hover:border-primary",
  hoverShadow: "hover:shadow-[0_0_40px_-10px_rgba(46,204,113,0.4)]",
  iconColor: "text-primary",
  bgGradient: "from-primary/20 to-transparent"
}, {
  title: "Razor-Sharp Edging",
  description: "Defining the boundaries of your landscape with surgical precision. Our edging creates clean, modern lines for driveways and paths.",
  icon: Scissors,
  colSpan: "md:col-span-1",
  accentColor: "var(--color-primary)",
  hoverBorder: "hover:border-primary",
  hoverShadow: "hover:shadow-[0_0_40px_-10px_rgba(46,204,113,0.4)]",
  iconColor: "text-primary",
  bgGradient: "from-primary/15 to-transparent"
}, {
  title: "Garden Maintenance",
  description: "Comprehensive plant health management and weed control. We ensure your garden beds remain vibrant, lush, and nutrient-rich year-round.",
  icon: Leaf,
  colSpan: "md:col-span-1",
  accentColor: "var(--color-primary)",
  hoverBorder: "hover:border-primary",
  hoverShadow: "hover:shadow-[0_0_40px_-10px_rgba(46,204,113,0.4)]",
  iconColor: "text-primary",
  bgGradient: "from-primary/15 to-transparent"
}, {
  title: "Acreage Care",
  description: "Large-scale mowing and maintenance for properties on acerage. Keeping expansive land clean, safe, and professionally maintained.",
  icon: Mountain,
  colSpan: "md:col-span-1",
  accentColor: "var(--color-primary)",
  hoverBorder: "hover:border-primary",
  hoverShadow: "hover:shadow-[0_0_40px_-10px_rgba(46,204,113,0.4)]",
  iconColor: "text-primary",
  bgGradient: "from-primary/15 to-transparent"
}, {
  title: "Property Care",
  description: "Complete exterior maintenance for residential and strata properties. Reliable, professional service that keeps your investment in pristine condition.",
  icon: Home,
  colSpan: "md:col-span-1",
  accentColor: "var(--color-primary)",
  hoverBorder: "hover:border-primary",
  hoverShadow: "hover:shadow-[0_0_40px_-10px_rgba(46,204,113,0.4)]",
  iconColor: "text-primary",
  bgGradient: "from-primary/15 to-transparent"
}];
export default function DigitalArsenal() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="47831" id="our-care-specialisations" className="dark bg-background text-foreground relative py-24 overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[160px] pointer-events-none opacity-40" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[160px] pointer-events-none opacity-40" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-16 max-w-3xl">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent">
            Our Care Specialisations
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-medium">
            We combine rugged performance with prestige precision. Explore our suite of professional property services designed to keep your landscape looking its absolute best.
          </p>
        </div>

        <div ref={ref} className={cn("grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {services.map((service, i) => <div key={service.title} data-index={i} className={cn("group relative rounded-3xl border border-white/10 bg-card backdrop-blur-xl p-8 transition-all duration-500", "hover:-translate-y-2", service.colSpan, service.hoverBorder, service.hoverShadow)}>
              {}
              <div className={cn("absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br pointer-events-none", service.bgGradient)} />

              <div className="relative z-10 flex flex-col h-full justify-between">
                <div className="mb-6 flex justify-between items-start">
                  <div className={cn("p-3 rounded-2xl bg-white/5 border border-white/10 shadow-inner backdrop-blur-md transition-colors duration-300 group-hover:bg-white/10", service.iconColor)}>
                    <service.icon className="w-8 h-8 drop-shadow-[0_0_12px_currentColor] animate-pulse" strokeWidth={1.5} />
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-white/20 group-hover:text-primary transition-colors duration-300" />
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground group-hover:text-white/90 transition-colors leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}